import { useCallback, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import './styles.css'
import { api } from './lib/api'
import type { Actor } from './lib/types'
import { navigate, useRoute } from './lib/router'
import { ToastProvider } from './components/Toaster'
import { ConfirmProvider } from './components/Confirm'
import { ProjectNavProvider } from './lib/projectNav'
import { Topbar } from './components/Topbar'
import { AuthScreen } from './screens/AuthScreen'
import { Dashboard } from './screens/Dashboard'
import { ProjectView } from './screens/ProjectView'
import { PostView } from './screens/PostView'
import { InviteView } from './screens/InviteView'
import { Button, CenterMessage, Spinner, TooltipProvider } from './ds'

function Routes({ actor }: { actor: Actor }) {
  const route = useRoute()
  switch (route.name) {
    case 'dashboard':
      return <Dashboard />
    case 'project':
      return <ProjectView projectId={route.projectId} actor={actor} />
    case 'post': {
      // Если в историю положили peekProjectId (клик по карточке на доске) — показываем
      // доску проекта с выехавшей справа peek-панелью. Прямой переход по ссылке
      // (новая вкладка / перезагрузка без state) открывает пост полной страницей.
      const peekProjectId = (window.history.state as { peekProjectId?: string } | null)?.peekProjectId
      if (peekProjectId) {
        return <ProjectView projectId={peekProjectId} actor={actor} peekPostId={route.postId} />
      }
      return <PostView postId={route.postId} actor={actor} />
    }
    case 'invite':
      return <InviteView projectId={route.projectId} actor={actor} />
    default:
      return (
        <CenterMessage
          wordmark="MENTIONS"
          title="Страница не найдена"
          sub="Такой страницы здесь нет."
          action={
            <Button variant="secondary" onClick={() => navigate('/')}>
              На дашборд
            </Button>
          }
        />
      )
  }
}

function App() {
  // undefined — ещё проверяем сессию; null — не авторизован; Actor — вошли.
  const [actor, setActor] = useState<Actor | null | undefined>(undefined)

  const loadMe = useCallback(() => {
    api
      .me()
      .then((res) => setActor(res.actor))
      .catch(() => setActor(null))
  }, [])

  useEffect(() => {
    loadMe()
  }, [loadMe])

  function logout() {
    api.logout().finally(() => {
      navigate('/')
      setActor(null)
    })
  }

  let body
  if (actor === undefined) {
    body = (
      <div className="loading-screen">
        <Spinner size={28} />
      </div>
    )
  } else if (actor === null) {
    body = <AuthScreen onAuthed={loadMe} />
  } else {
    body = (
      <div className="app">
        <Topbar actor={actor} onLogout={logout} onActorChange={setActor} />
        <Routes actor={actor} />
      </div>
    )
  }

  return (
    <TooltipProvider>
      <ToastProvider>
        <ConfirmProvider>
          <ProjectNavProvider>{body}</ProjectNavProvider>
        </ConfirmProvider>
      </ToastProvider>
    </TooltipProvider>
  )
}

const container = document.getElementById('root')!
const globalWithRoot = globalThis as typeof globalThis & {
  __mrktngMentionsRoot?: ReturnType<typeof createRoot>
}
const root = globalWithRoot.__mrktngMentionsRoot || createRoot(container)
globalWithRoot.__mrktngMentionsRoot = root
root.render(<App />)
